import {
  Calendar,
  CheckCircle2,
  Globe,
  Hash,
  Pencil,
  Sparkles,
  Store,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import type { Workspace } from "@artisancode/api-types";

interface CurrentWorkspaceIdentityCardProps {
  workspace: Workspace;
  canUpdate: boolean;
  onOpenEdit: () => void;
}

export function CurrentWorkspaceIdentityCard({
  workspace,
  canUpdate,
  onOpenEdit,
}: CurrentWorkspaceIdentityCardProps) {
  const formattedDate = workspace.createdAt
    ? new Date(workspace.createdAt).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  return (
    <Card className="shadow-xs">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
        <div className="flex items-center gap-3.5">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Store className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-xl font-bold">
                {workspace.name}
              </CardTitle>
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs gap-1"
              >
                <CheckCircle2 className="h-3 w-3" />
                Workspace Aktif
              </Badge>
              {workspace.isMain && (
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200 text-xs gap-1"
                >
                  <Sparkles className="h-3 w-3 text-amber-500" />
                  Cabang Utama
                </Badge>
              )}
            </div>
            <CardDescription className="text-xs mt-1">
              Pengaturan khusus dan identitas operasional untuk outlet ini.
            </CardDescription>
          </div>
        </div>

        {canUpdate && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenEdit}
            className="gap-1.5 self-start sm:self-auto"
          >
            <Pencil className="h-3.5 w-3.5" />
            <span>Edit Info Toko</span>
          </Button>
        )}
      </CardHeader>

      <CardContent className="pt-2 border-t">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border bg-muted/20 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Store className="h-3.5 w-3.5" />
              <span>Nama Outlet / Cabang</span>
            </div>
            <p className="font-semibold text-sm text-foreground">
              {workspace.name}
            </p>
          </div>

          <div className="rounded-lg border bg-muted/20 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Globe className="h-3.5 w-3.5" />
              <span>Slug URL / Identifier</span>
            </div>
            <p className="font-mono font-medium text-sm text-foreground">
              {workspace.slug}
            </p>
          </div>

          <div className="rounded-lg border bg-muted/20 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>Terdaftar Sejak</span>
            </div>
            <p className="text-sm font-medium text-foreground">
              {formattedDate}
            </p>
          </div>

          <div className="rounded-lg border bg-muted/20 p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Hash className="h-3.5 w-3.5" />
              <span>Workspace ID</span>
            </div>
            <p
              className="font-mono text-xs text-muted-foreground truncate"
              title={workspace.id}
            >
              {workspace.id}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
